import { cssFont } from '@/utils/substituteFont';
import type { MeasureText } from '@/utils/substituteLayout';

/**
 * Size text is measured at. Canvas font sizes are rounded by some engines, so a substitute is
 * measured at one comfortable size and scaled linearly, which advance widths are.
 */
const REFERENCE_SIZE = 100;

/** An average text face's advance in ems, for measuring where no canvas exists (jsdom, a lost context). */
const FALLBACK_ADVANCE_EM = 0.55;

let measuringContext: CanvasRenderingContext2D | null | undefined;

const contextForMeasuring = (): CanvasRenderingContext2D | null => {
  if (measuringContext === undefined) {
    measuringContext = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    // The substitute is drawn without kerning (`SubstituteText`), so it is measured without it too.
    if (measuringContext) measuringContext.fontKerning = 'none';
  }
  return measuringContext;
};

/**
 * Width of `text` set in `font` at `fontSize`, in the unit of the size.
 *
 * The fonts are the reviewer's own installed ones or the document's embedded ones pdf.js loaded for
 * the paint (`utils/substituteFont`), so nothing has to load before they can be measured.
 */
export const measureSubstitute: MeasureText = (text, fontSize, font) => {
  const context = contextForMeasuring();
  if (!context) return text.length * FALLBACK_ADVANCE_EM * fontSize;
  context.font = cssFont(font, REFERENCE_SIZE);
  return (context.measureText(text).width * fontSize) / REFERENCE_SIZE;
};
